import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchEmployeeInput } from "@/components/overtime/components/SearchEmployeeInput";
import { User, UserRole } from "@/types";
import { AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { inviteService } from "@/services/inviteService";

/**
 * Data structure for role assignment
 */
interface RoleAssignmentData {
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  newEmail: string;
  department: string;
  companyId: string;
}

interface AssignRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userRole?: UserRole;
  userDepartment?: string[];
  companyId?: string | null;
  onRoleAssign?: (data: RoleAssignmentData) => Promise<void>;
}

/**
 * Modal component for department heads to assign roles to employees
 * Features employee search with department filtering and email input
 * 
 * FLOW:
 * 1. Department Head triggers invite-superintendent (this modal)
 * 2. Supabase sends invite email automatically
 * 3. New Superintendent's first login triggers ensure-profile (AuthContext)
 */
export function AssignRoleModal({
  open,
  onOpenChange,
  userRole,
  userDepartment,
  companyId,
  onRoleAssign
}: AssignRoleModalProps) {
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ employee?: string; email?: string }>({});

  // Reset form when modal opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form when closing
      setSelectedEmployee(null);
      setEmail("");
      setErrors({});
    }
    onOpenChange(newOpen);
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors: { employee?: string; email?: string } = {};

    if (!selectedEmployee) {
      newErrors.employee = "Please select an employee";
    }

    if (!email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Call the invite-superintendent Supabase Edge Function
   * This function:
   * 1. Creates user in auth.users with metadata
   * 2. Sends magic link email that goes directly to dashboard
   * 3. Database trigger automatically creates profile
   */
  const callInviteSuperintendent = async (): Promise<{ success: boolean; error?: string; data?: { email: string; company: string } }> => {
    try {
      // Build payload
      const department = Array.isArray(selectedEmployee?.department) 
        ? selectedEmployee.department[0] || null
        : selectedEmployee?.department || null;

      if (!department) {
        throw new Error('Employee must have a department assigned');
      }

      const payload = {
        email: email.trim().toLowerCase(),
        employee_name: selectedEmployee?.name?.trim() || '',
        first_name: selectedEmployee?.firstName?.trim() || "",
        last_name: selectedEmployee?.lastName?.trim() || "",
        department
      };

      if (!payload.employee_name) {
        throw new Error('Employee name is required');
      }

      // ✅ Use the centralized invite service
      const result = await inviteService.inviteSuperintendent(payload);
      
      if (result.success) {
        console.log('✅ Superintendent invited successfully:', result.data);
      } else {
        console.error('❌ Superintendent invitation failed:', result.error);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      return { success: false, error: errorMessage };
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Call the Supabase Edge Function
      const result = await callInviteSuperintendent();
      
      if (result.success) {
        const departmentName = Array.isArray(selectedEmployee?.department) 
          ? selectedEmployee?.department[0] || 'Unknown Department'
          : selectedEmployee?.department || 'Unknown Department';
        
        const responseData = result.data;
        const invitedEmail = responseData?.email || email.trim();
        
        // Show detailed success message
        toast.success(
          `✅ ${selectedEmployee?.name} has been successfully invited as superintendent of ${departmentName} department!`,
          { duration: 6000 }
        );
        
        // Show email confirmation
        toast.success(
          `📧 Invitation email sent to ${invitedEmail}! They can now sign in directly and access the dashboard.`,
          { duration: 8000 }
        );

        // Success metrics logging removed for production
        
        // Update local state to reflect new superintendent if callback provided
        if (onRoleAssign && selectedEmployee && companyId) {
          try {
            await onRoleAssign({
              employeeId: selectedEmployee.id,
              employeeName: selectedEmployee.name,
              employeeEmail: selectedEmployee.email,
              newEmail: invitedEmail,
              department: departmentName,
              companyId
            });
            // Local state update logging removed for production
          } catch (error) {
            console.warn('Failed to update local state (non-critical):', error);
            // Don't block the success flow if state update fails
          }
        }
        
        // Close modal on success
        handleOpenChange(false);
      } else {
        // Enhanced error handling
        const errorMessage = result.error || 'An unexpected error occurred';
        
        console.error('Superintendent invitation failed:', {
          error: errorMessage,
          employee: selectedEmployee?.name,
          email: email.trim(),
          department: selectedEmployee?.department
        });
        
        // Provide specific error messages based on error content
        if (errorMessage.includes('already exists')) {
          toast.error(
            `❌ Email ${email.trim()} is already registered. Please use a different email address.`,
            { duration: 6000 }
          );
        } else if (errorMessage.includes('permission') || errorMessage.includes('privileges')) {
          toast.error(
            `❌ You don't have permission to invite superintendents. Please contact your administrator.`,
            { duration: 6000 }
          );
        } else if (errorMessage.includes('department')) {
          toast.error(
            `❌ Department assignment issue: ${errorMessage}`,
            { duration: 6000 }
          );
        } else if (errorMessage.includes('company')) {
          toast.error(
            `❌ Company validation failed. Please contact support.`,
            { duration: 6000 }
          );
        } else if (errorMessage.includes('configuration') || errorMessage.includes('Database')) {
          toast.error(
            `❌ System configuration error. Please contact support.`,
            { duration: 8000 }
          );
        } else {
          toast.error(
            `❌ Failed to send invitation: ${errorMessage}`,
            { duration: 6000 }
          );
        }
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      toast.error('An unexpected error occurred. Please try again.', { duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear email error when user starts typing
    if (errors.email && value.trim()) {
      setErrors(prev => ({ ...prev, email: undefined }));
    }
  };

  const handleEmployeeChange = (employee: User | null) => {
    setSelectedEmployee(employee);
    
    // Clear employee error when user selects an employee
    if (errors.employee && employee) {
      setErrors(prev => ({ ...prev, employee: undefined }));
    }
  };

  const isFormValid = !!selectedEmployee && !!email.trim() && Object.keys(errors).length === 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-[500px] p-6">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold">Make Superintendent</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Employee Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Employee</Label>
            <SearchEmployeeInput 
              value={selectedEmployee?.id || ''}
              onChange={handleEmployeeChange}
              userRole={userRole}
              userDepartment={userDepartment}
              hideLabel
            />
            {selectedEmployee && (
              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 mt-1">
                <CheckCircle className="h-3 w-3" />
                Selected: {selectedEmployee.name}
              </div>
            )}
            {errors.employee && (
              <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 mt-1">
                <AlertCircle className="h-3 w-3" />
                {errors.employee}
              </div>
            )}
          </div>
          
          {/* Department Display */}
          {selectedEmployee && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Department</Label>
              <div className="h-11 px-3 py-2 bg-muted border border-input rounded-md text-sm flex items-center">
                <span className="text-foreground">
                  {Array.isArray(selectedEmployee.department) 
                    ? selectedEmployee.department[0] || 'Not assigned'
                    : selectedEmployee.department || 'Not assigned'}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                This employee will be appointed as superintendent for this department
              </div>
            </div>
          )}
          
          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email Address
            </Label>
            
            {/* Verification Alert */}
            <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-800 dark:text-blue-200">
                  <p>This email will be used for employee verification. Please ensure you enter the correct email address.</p>
                </div>
              </div>
            </div>
            
            <Input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter email address"
              className={`h-11 ${errors.email ? 'border-red-500 focus:border-red-500' : ''}`}
            />
            {errors.email && (
              <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 mt-1">
                <AlertCircle className="h-3 w-3" />
                {errors.email}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="min-w-[80px]"
          >
            {isSubmitting ? "Inviting..." : "Invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
